import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, AlertCircle, Grid, Image as ImageIcon, ArrowRight } from 'lucide-react';
import ZoomableImage from '../components/ZoomableImage';
import SEO from '../components/SEO';

// Fallback mock data in case API is unreachable
const fallbackReferencer = [
  {
    id: '1',
    titel: 'Eksklusiv Villaindkørsel',
    beskrivelse: 'Eksklusiv dansk villaindkørsel udført i natursten med en elegant, fugefri finish. Den våde overflade fremhæver stenenes naturlige farvespil.',
    kategori: 'Indkørsel',
    billede: ['https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/edfc4191-0236-4b5a-915a-2a04051a9ea3.jpg']
  },
  {
    id: '2',
    titel: 'Moderne Terrasse',
    beskrivelse: 'Terrasse med fugefri stenbelægning i lyse natursten. Giver et stilrent og moderne udtryk med ekstremt høj finish, der passer perfekt til havemøblerne.',
    kategori: 'Terrasse',
    billede: ['https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/badd6eea-b45e-42e2-bf84-1f92e8d91be3.jpg']
  },
  {
    id: '3',
    titel: 'Renoveret Trappeforløb',
    beskrivelse: 'Trappe renoveret med helstøbt stenbelægning på både trin og lodrette flader. En slidstærk og skridsikker løsning uden synlige samlinger.',
    kategori: 'Trappe',
    billede: ['https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/aaacfe6e-3946-4690-a3d7-a4f1cfdfe45e.jpg']
  },
  {
    id: '4',
    titel: 'Natursten i Resin (Struktur)',
    beskrivelse: 'Nærbillede af små natursten bundet i en stærk, klar resin. Den faste overflade sikrer en skridsikker tekstur og minimal vedligeholdelse uden løse sten.',
    kategori: 'Detalje',
    billede: ['https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/d9e66450-9d19-4e2f-89b7-8625a6886679.jpg']
  }
];

export default function Referencer() {
  const [referencer, setReferencer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedKategori, setSelectedKategori] = useState('Alle');

  useEffect(() => {
    const fetchReferencer = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/referencer');
        if (!response.ok) {
          throw new Error('Kunne ikke hente referencer');
        }
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          setReferencer(data);
        } else {
          setReferencer(fallbackReferencer);
        }
      } catch (err) {
        console.error('Fejl ved hentning af referencer fra CMS:', err);
        // Fallback to local mock data
        setReferencer(fallbackReferencer);
      } finally {
        setLoading(false);
      }
    };

    fetchReferencer();
  }, []);

  // Extract unique categories for filtering
  const kategorier = ['Alle', ...new Set(referencer.map((ref) => ref.kategori))];

  const filteredReferencer = selectedKategori === 'Alle'
    ? referencer
    : referencer.filter((ref) => ref.kategori === selectedKategori);

  return (
    <div className="pt-24 md:pt-40 xl:pt-28 pb-16">
      <SEO
        title="Referencer & Inspiration | Se Udførte Stenbelægninger | Stenbelægning.dk"
        description="Gå på opdagelse i vores galleri over gennemførte projekter med helstøbt stenbelægning. Se eksempler på indkørsler, terrasser, trapper og havestier i hele Danmark."
      />
      {/* Header Section */}
      <section className="bg-stone-50 py-16 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-stone-900 tracking-tight mt-2">
              Referencer
            </h1>
            <p className="text-xl text-stone-600 mt-4 font-medium">
              Se tidligere projekter
            </p>
            <p className="text-stone-500 mt-4 leading-relaxed">
              Her vises billeder af udførte indkørsler, terrasser, trapper og gangarealer. Helstøbt stenbelægning udført i natursten med elegant, fugefri finish.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="h-10 w-10 text-emerald-600 animate-spin" />
              <p className="text-stone-500 mt-4 font-medium">Henter referenceprojekter...</p>
            </div>
          ) : (
            <>
              {/* Category Filter Buttons */}
              <div className="flex flex-wrap gap-2 justify-center mb-12">
                {kategorier.map((kat) => (
                  <button
                    key={kat}
                    onClick={() => setSelectedKategori(kat)}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                      selectedKategori === kat
                        ? 'bg-stone-900 text-white'
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                    }`}
                  >
                    {kat}
                  </button>
                ))}
              </div>

              {/* Projects Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {filteredReferencer.map((ref) => {
                  const imageUrl = Array.isArray(ref.billede) && ref.billede[0] 
                    ? ref.billede[0] 
                    : 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/edfc4191-0236-4b5a-915a-2a04051a9ea3.jpg';
                  
                  return (
                    <Link 
                      key={ref.id} 
                      to={`/referencer/${ref.id}`}
                      className="bg-white overflow-hidden border border-stone-100 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col group"
                    >
                      {/* Image Container */}
                      <ZoomableImage
                        src={imageUrl}
                        alt={ref.titel}
                        className="aspect-video bg-stone-100"
                        zoomScale={2.5}
                        optimizedWidth={800}
                      />

                      {/* Content */}
                      <div className="p-6 flex-1 flex flex-col justify-between">
                        <div>
                          <h3 className="text-xl font-bold text-stone-900 mb-2 group-hover:text-black transition-colors">
                            {ref.titel}
                          </h3>
                          <p className="text-stone-600 text-sm leading-relaxed mb-4">
                            {ref.beskrivelse}
                          </p>
                        </div>
                        <div className="border-t border-stone-50 pt-4 flex items-center justify-between text-xs font-medium">
                          <span className="text-stone-400">
                            Helstøbt stenbelægning udført i natursten.
                          </span>
                          <span className="text-stone-900 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                            Se projekt <ArrowRight className="h-3.5 w-3.5" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>

              {filteredReferencer.length === 0 && (
                <div className="text-center py-16 bg-stone-50 border border-stone-100">
                  <ImageIcon className="h-12 w-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500 font-medium">Ingen referencer fundet i denne kategori.</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 text-white mt-12 w-full overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/f0f16b94-8f6e-4a76-b4de-669e74e9d9c5.jpg" 
            alt="Fugefri stenbelægning" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-stone-950/85 backdrop-blur-[2px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Vil du have et lignende resultat?
          </h2>
          <p className="mt-4 text-lg sm:text-xl max-w-2xl mx-auto text-stone-300">
            Kontakt os i dag for at få vurderet dit projekt og modtage et uforpligtende tilbud på en helstøbt stenbelægning.
          </p>
          <div className="mt-8">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl text-base font-bold bg-emerald-600 text-white hover:bg-emerald-700 hover:-translate-y-1 transition-all duration-300"
            >
              Få et uforpligtende tilbud
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
